<template>
    <layout>
        <template v-slot:header>
            <AppHead
                :title="`Shipments ${user.company_name ? user.company_name : user.first_name + ' ' + user.last_name}`" />
            <h3><i class="fas fa-boxes text-secondary mr-2"></i>Shipments for {{ user.company_name || '' }} {{
                user.first_name || '' }} {{ user.last_name || '' }}</h3>
            <BreadCrumbs activeItem="Shipment" :crumbs="{ home: 'Dashboard', 'admin.orders.ship': 'Ship Orders' }" />
            <flash-messages />
        </template>

        <template v-slot:default>
            <div class="row">
                <div class="col-12 col-md-6 col-lg-4 p-0 mb-4">
                    <div v-if="!mtd.mtd_ship_approved">
                        <div class="alert alert-danger"><strong>Admin Approval Required - Do Not Ship Items</strong></div>

                        <card title="Super Admin Only" class="mb-3" headerStyle="warning" v-if="isSuper">
                            <div class="form-check pl-0 d-flex align-items-center">
                                <input type="checkbox" id="mtnApproveShip" class="mr-2" @click="mtdSetShipApproved()" />
                                <label class="mb-0" for="mtnApproveShip">Approve</label>
                            </div>
                        </card>
                    </div>

                    <card :title="`Info for ${user.company_name || ''}  ${user.first_name || ''} ${user.last_name || ''}`">
                        <div class="mb-3" v-if="user.mtd_info">
                            <p class="mb-0 font-weight-bold">Tool User under MTD:</p>
                            <p class="mb-0 text-secondary"><inertia-link :href="route('mtd.view', user.mtd_info.id)">{{
                                user.mtd_info.company_name }}</inertia-link> -
                                <Phone v-model="user.mtd_info.phone_number" />
                            </p>
                        </div>
                        <div v-else>
                            <p class="mb-0 font-weight-bold">Company</p>
                            <p class="mb-0 text-secondary"><inertia-link :href="route('mtd.view', user.id)">{{
                                user.company_name }}</inertia-link></p>
                            <p class="mb-0 font-weight-bold">User Phone Number:</p>
                            <p class="mb-0 text-secondary">
                                <Phone v-model="user.phone_number" />
                            </p>
                        </div>
                    </card>
                </div>
                <div class="col-md-4 col-lg-4">
                    <a :href="route('admin.pastshipments', user.id)" target="_blank" class="btn btn-primary">View past
                        shipments</a>
                </div>
            </div>

            <section class="notes mt-3" v-if="notes.length">
                <h3><i class="fa fa-exclamation-circle text-secondary mr-2"></i>Notes from User</h3>

                <div v-for="note in notes" :key="note.id" class="mb-3">
                    <card>
                        {{ moment(note.created_at).format('M/D/YY h:mm A') }} on <a
                            :href="route('admin.order.view', note.order.id)" target="_new">{{ note.order.id }}</a>: {{
                                note.content }}
                    </card>
                </div>
            </section>

            <section class="shipments mt-3" :class="[(shipments.length == 1) ? 'shipment-active' : 'multiple']">
                <h3><i class="fas fa-clock text-secondary mr-2"></i>Pending Shipments</h3>
                <div v-for="shipment in shipments" :key="shipment.id" class="mb-3">
                    <card headerStyle="dark"
                        :title="`#${shipment.id} - Opened at: ${moment(shipment.date).format('M/D/YY h:mm A')}`"
                        icon="fas fa-tags">
                        <div class="alert alert-danger" v-if="shipment.errors.length > 0">
                            <div v-for="error in shipment.errors" :key="error">
                                {{ error }}
                            </div>
                        </div>

                        <h4>Ship To</h4>
                        <div class="ship-address d-flex">
                            <div class="text-secondary">
                                <p class="mb-0">{{ shipment.address.company_name }}</p>
                                <p class="mb-0">{{ shipment.address.first_name }} {{ shipment.address.last_name }}</p>
                                <p class="mb-0">{{ shipment.address.address1 }}</p>
                                <p class="mb-0">{{ shipment.address.address2 }}</p>
                                <p class="mb-0">{{ shipment.address.city }}, {{ shipment.address.state }} {{
                                    shipment.address.postcode }}</p>
                            </div>
                        </div>

                        <hr>

                        <h4>Packages</h4>

                        <div v-for="(box, index) in shipment.packages" :key="box.id" class="box-item">
                            <hr v-if="index !== 0">

                            <div v-if="shipment.status == 1">
                                <h5 class="font-weight-bold"> Package #{{ index + 1 }}</h5>
                                <input type="radio" :value="box.id" v-model="selectedPackage" :id="'package-' + box.id">
                                <label :for="'package-' + box.id"> Add Items to this Package </label>

                                <div class="ml-3">
                                    <div class="row">
                                        <div class="col-12 col-lg-6 mb-3 mb-lg-0">
                                            <select class="form-control mb-2" v-model="box.type"
                                                @change="setBox(box, shipment)">
                                                <option v-for="type in box_types" :key="type.code" :value="type.code">{{
                                                    type.code }}: {{ type.width }} x {{ type.length }} x {{ type.depth }} {{
        type.one_rate ? '(one-rate)' : '' }}</option>
                                            </select>
                                            <a :href="route('admin.boxes')" target="_new">Manage Boxes <i
                                                    class="fas fa-external-link-alt"></i></a>
                                        </div>

                                        <div class="col-12 col-lg-6">
                                            <div class="d-flex w-100">
                                                <label class="mr-2">Weight:</label>
                                                <input class="form-control custom-form-inline" type="text"
                                                    v-model="box.weight" @input="setWeight(box)" />
                                            </div>
                                            <span class="text-secondary">Estimated Weight: {{ box.estimated_weight }}</span>
                                        </div>
                                    </div>

                                    <ul class="items list-group">
                                        <li v-for="item in box.items"
                                            class="d-flex align-items-start align-items-lg-end flex-column flex-lg-row list-group-item"
                                            :key="item.id">
                                            <div class="d-flex align-items-end mb-2 mb-lg-0">
                                                <jet-button btnStyle="danger" class="mr-3" @click="deleteItem(item)"><i
                                                        class="fa fa-trash text-white"></i></jet-button>
                                                <div class="mx-0 mx-lg-2">
                                                    <label class="form-label text-secondary mb-0">Update Qty</label>
                                                    <input type="text" class="form-control custom-form-inline"
                                                        v-model="item.qty" @input="updateItem(item)" />
                                                </div>
                                                <div class="p-inputgroup flex-1 packages-move d-flex align-items-center ml-3"
                                                    v-if="shipment.packages.length > 1">
                                                    <Dropdown v-model="item.moveToPackage"
                                                        :options="getShipmentPackageOptions(shipment, box)"
                                                        placeholder="Move to" class="w-full md:w-14rem" optionLabel="label"
                                                        optionValue="value" size="sm" />
                                                    <jet-button
                                                        :disabled="formMovePackage.processing || !item.moveToPackage"
                                                        :loading="formMovePackage.processing" class="mr-3"
                                                        @click="moveToPackage(shipment, box, item)">Confirm
                                                        Move</jet-button>
                                                </div>
                                                <jet-button class="mr-3" @click="moveItem(shipment, item, index + 1)"><i
                                                        class="fa fa-arrow-down text-white"></i></jet-button>
                                                <jet-button class="mr-3" @click="moveItem(shipment, item, index - 1)"
                                                    v-if="index > 0"><i class="fa fa-arrow-up text-white"></i></jet-button>
                                            </div>
                                            <div class="mx-0 mx-lg-2">
                                                <p class="text-secondary mb-0">{{ item.sku }}</p>
                                                <p class="text-secondary mb-0"> {{ item.name }}</p>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div v-else>
                                <p class="font-weight-bold mb-2">{{ box.type }}</p>
                                <p class="m-0">{{ box.weight }} lbs</p>
                                <div class="items">
                                    <DataTable :value="box.items" sortField="sku" :sortOrder="-1"
                                        class="table-striped table-bordered w-100 w-md-50" dataKey="id">
                                        <Column field="qty" header="Qty" :sortable="true"></Column>
                                        <Column field="sku" header="SKU" :sortable="true"></Column>
                                        <Column field="name" header="Name"></Column>
                                    </DataTable>
                                </div>
                            </div>

                            <div class="mt-2">
                                <p class="m-0">{{ box.box_size }}</p>
                                <a :href="box.label" target="_label" v-if="shipment.status == 2">Shipping Label</a> |
                                <a :href="route('admin.orders.packing', box)" target="_label">Packing Slip</a>
                            </div>
                        </div>

                        <template #footer>
                            <div v-if="shipment.status == 1">
                                <div class="row align-items-end">
                                    <div class="col-12 col-lg-4">
                                        <jet-button icon="fas fa-dollar-sign" class="m-w-100 mt-2 mt-lg-0"
                                            @click="getRates(shipment)" :disabled="requestingRate"
                                            :loading="requestingRate">Estimate Rates</jet-button>
                                    </div>
                                </div>

                                <div v-if="shipment.ratesFedex">
                                    <div v-for="rate in shipment.ratesFedex" :key="rate.service_id">
                                        {{ formatDollars(rate.cost) }} -- {{ rate.service_id }} -- {{ rate.label }}
                                    </div>
                                </div>
                                <div v-if="shipment.ratesUps">
                                    <div v-for="rate in shipment.ratesUps" :key="rate.service_id">
                                        {{ formatDollars(rate.cost) }} -- {{ rate.service_id }} -- {{ rate.label }}
                                    </div>
                                </div>
                                <hr />
                            </div>
                            <div v-if="shipment.status == 2">
                                <div class="row align-items-end my-3">
                                    <div class="col-12 col-lg-8">
                                        <label class="form-label mb-0">Selected Method: {{ shipment.method_label }} -
                                            Override Method:</label>
                                        <select class="form-control" v-model="form.shipping_method_id">
                                            <option v-for="(label, code) in fedex_methods" :key="code" :value="code">FedEx:
                                                {{ code }}: {{ label }}</option>
                                            <option v-for="(label, code) in ups_methods" :key="code" :value="code">UPS: {{
                                                code }}: {{ label }}</option>
                                        </select>
                                    </div>

                                    <div class="col-12 col-lg-4">
                                        <jet-button icon="fas fa-dollar-sign" class="m-w-100 mt-2 mt-lg-0"
                                            @click="getRates(shipment)" :disabled="requestingRate"
                                            :loading="requestingRate">Estimate Rates</jet-button>
                                    </div>
                                </div>

                                <div v-if="shipment.ratesFedex">
                                    <div v-for="rate in shipment.ratesFedex" :key="rate.service_id">
                                        {{ formatDollars(rate.cost) }} -- {{ rate.service_id }} -- {{ rate.label }}
                                    </div>
                                </div>
                                <div v-if="shipment.ratesUps">
                                    <div v-for="rate in shipment.ratesUps" :key="rate.service_id">
                                        {{ formatDollars(rate.cost) }} -- {{ rate.service_id }} -- {{ rate.label }}
                                    </div>
                                </div>

                                <jet-button btnStyle="danger" class="mt-3 mr-2" @click="reopenShipment(shipment)">Reopen
                                    Shipment</jet-button>

                                <jet-button class="mt-3" @click="prepareShipment(shipment)">Prepare Shipment</jet-button>

                                <div class="mt-3">
                                    Manual Actions:
                                    <div v-if="shipment.status == 1 || shipment.status == 2 || shipment.status == 20">
                                        <inertia-link :href="route('admin.user.shipment.shipped', shipment)"
                                            preserve-scroll>Mark Shipped</inertia-link><br />
                                    </div>
                                    <div v-else-if="shipment.status == 6">
                                        <inertia-link :href="route('admin.user.shipment.delivered', shipment)"
                                            preserve-scroll>Mark Delivered</inertia-link><br />
                                    </div>
                                </div>
                            </div>
                            <div v-else>

                                <jet-button class="m-w-100 mr-3" icon="fas fa-plus" @click="addBox(shipment)">Add Shipment
                                    Package</jet-button>
                                <jet-button class="m-w-100" icon="fas fa-window-close" btnStyle="success"
                                    @click="closeShipment(shipment)">Close Shipment</jet-button>
                                <jet-button class="m-w-100 float-right" icon="fas fa-window-close" btnStyle="danger"
                                    @click="cancelShipment(shipment)">Cancel Shipment</jet-button>
                            </div>
                        </template>
                    </card>
                </div>
            </section>

            <card title="Pending Items">
                <div class="w-100 list-action-buttons sticky" v-if="selectedAddress" style="position: sticky">
                    <jet-button :disabled="form.processing" icon="fas fa-box" btnStyle="success" @click="createShipment()"
                        class="mr-2 m-w-100"><span v-if="selectedPackage">Add to Package</span>
                        <span v-else>Create Shipment</span></jet-button>
                    <hr />
                    <Calendar placeholder="Backorder ETA" class="m-w-100" v-model="form.backorder_eta" :minDate="today"
                        :showIcon="true" :selectOtherMonths="true" />
                    <jet-button :disabled="form.processing" btnStyle="success" class="ml-0 ml-md-2 mt-2 mt-md-0 m-w-100"
                        @click="createBackorder()">Set Items Backordered</jet-button>
                    <hr />
                    <jet-button :disabled="form.processing" icon="fas fa-plus" btnStyle="success"
                        class="ml-0 ml-lg-2 mt-2 mt-md-0 m-w-100" @click="createDropship()">Create Dropship
                        Shipment</jet-button>
                </div>
                <div class="table-responsive pt-2">
                    <DataTable :value="pendingItems" :paginator="false" class="table table-striped" dataKey="id">
                        <Column class="button-container" headerStyle="width: 3em">
                            <template #body="slotProps">
                                <div v-if="!selectedAddress || selectedAddress == slotProps.data.address_id">
                                    <input type="checkbox" v-bind:value="slotProps.data.id" :key="slotProps.data.id"
                                        :checked="slotProps.data.selected" @input="selectItem($event, slotProps.data)"
                                        v-if="!slotProps.data.from_inventory && slotProps.data.qty_shipping > 0" />
                                    <div v-if="slotProps.data.has_returns">
                                        <h5 class="text-underline"><u>ORDER HAS RETURNS FOR THIS ITEM -- CHECK FIRST!!!</u>
                                        </h5>
                                    </div>
                                </div>
                            </template>
                        </Column>
                        <Column field="order_id" header="Order #" sortable>
                            <template #body="slotProps">
                                <a :href="route('admin.order.view', slotProps.data.order_id)" target="_new">{{
                                    slotProps.data.order_id + ' ' + slotProps.data.order_address }}</a>
                                <OrderItemPaymentIntent class="ml-2" :orderItem="slotProps.data" />
                                <Tags :tags="slotProps.data.order_tags"></Tags>
                                <DateTimeText v-model="slotProps.data.order_future_date" prepend="Future Fulfill"
                                    class="badge badge-info text-white ml-1 p-1"></DateTimeText>
                            </template>
                        </Column>
                        <Column field="product_sku" header="SKU" sortable>
                            <template #body="slotProps">
                                <a :href="route('admin.products.view', slotProps.data.product_id)" target="_new">{{
                                    slotProps.data.product_sku }}</a>
                                <div v-if="slotProps.data.from_bundle">
                                    <strong>From Bundle: {{ slotProps.data.from_bundle.sku }}</strong>
                                </div>
                            </template>
                        </Column>
                        <Column field="product_name" header="Name" sortable></Column>
                        <Column field="shippedDesc" header="Info"></Column>
                        <Column field="qty_shipping" header="Remaining" sortable>
                            <template #body="slotProps">
                                <span :class="getStatus(slotProps.data)">{{ slotProps.data.qty_shipping }}</span>
                            </template>
                        </Column>
                        <Column field="qty_shipping" header="Inventory">
                            <template #body="slotProps">
                                <p class="m-0">
                                    <InventoryInfo :inventoryInfo="slotProps.data.inventoryInfo" />
                                </p>
                            </template>
                        </Column>
                        <Column field="qty_shipping" header="Qty to Ship">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.qty_shipping == 0">
                                    In Shipments
                                </span>
                                <input type="text" class="form-control custom-form-inline"
                                    v-model="slotProps.data.qty_shipping" v-else />
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <div class="w-100 list-action-buttons" v-if="selectedAddress">
                    <jet-button :disabled="form.processing" icon="fas fa-box" btnStyle="success" @click="createShipment()"
                        class="mr-2 m-w-100"><span v-if="selectedPackage">Add to Package</span>
                        <span v-else>Create Shipment</span></jet-button>
                    <hr />
                    <Calendar placeholder="Backorder ETA" class="m-w-100" v-model="form.backorder_eta" :minDate="today"
                        :showIcon="true" :selectOtherMonths="true" />
                    <jet-button :disabled="form.processing" btnStyle="success" class="ml-0 ml-md-2 mt-2 mt-md-0 m-w-100"
                        @click="createBackorder()">Set Items Backordered</jet-button>
                    <hr />
                    <jet-button :disabled="form.processing" icon="fas fa-plus" btnStyle="success"
                        class="ml-0 ml-lg-2 mt-2 mt-md-0 m-w-100" @click="createDropship()">Create Dropship
                        Shipment</jet-button>
                </div>

            </card>
        </template>
    </layout>
</template>

<script>
import Layout from '@/Shared/Admin/Layout'
import FlashMessages from '@/Shared/FlashMessages'
import JetDialogModal from '@/Jetstream/DialogModal'
import JetDangerButton from '@/Jetstream/DangerButton'
import JetButton from '@/Jetstream/Button'
import moment from 'moment'
import Calendar from 'primevue/calendar'
import Dropdown from 'primevue/dropdown';
import BreadCrumbs from '@/Shared/BreadCrumbs'
import Card from "@/Shared/Card";
import AppHead from "@/Shared/AppHead";
import OrderItemPaymentIntent from '@/Components/General/OrderItemPaymentIntent'
import Tags from '@/Components/General/Tags'
import InventoryInfo from '@/Components/General/InventoryInfo'

export default {
    components: {
        FlashMessages,
        Layout,
        JetDialogModal,
        JetDangerButton,
        Calendar,
        Dropdown,
        BreadCrumbs,
        JetButton,
        Card,
        AppHead,
        OrderItemPaymentIntent,
        Tags,
        InventoryInfo,
    },
    props: {
        user: Object,
        items: Array,
        shipments: Array,
        notes: Array,
        box_types: Array,
        addresses: Array,
        fedex_methods: Object,
        ups_methods: Object,
        mtd: Object,
        isSuper: Boolean,
    },
    data() {
        return {
            selectedPackage: null,
            selectedAddress: null,
            shipItem: null,
            today: (new Date()),
            form: this.$inertia.form({
                box_size: null,
                weight: null,
                qty: null,
                shipping_method_id: null,
                parent_shipment_id: null,
                items: null,
                backorder_eta: null
            }),
            formNote: this.$inertia.form({
                _method: 'put',
                content: null,
                visible_mtd: 0,
                notify_mtd: 0,
                visible_tu: 0,
                notify_tu: 0
            }),
            formCancelShipment: this.$inertia.form({}),
            formAddBox: this.$inertia.form({}),
            formCloseShipment: this.$inertia.form({}),
            formReopenShipment: this.$inertia.form({}),
            inGetRate: false,
            requestingRate: false,
            formMovePackage: this.$inertia.form({
                qty: null,
                old_package_id: null,
                new_package_id: null,
                product_id: null,
            }),
        }
    },
    created() {
        this.moment = moment
    },
    computed: {
        pendingItems() {
            return this.items.filter(item => item.qty_shipping > 0)
                .map(item => {
                    if (item.order_future_date) {
                        const date = new Date(item.order_future_date)
                        item.tmp_order_future_date = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 7);
                    } else {
                        item.tmp_order_future_date = new Date();
                    }
                    return item
                })
                .sort((a, b) => {
                    return a.tmp_order_future_date - b.tmp_order_future_date
                })
        },
    },
    methods: {
        getStatus(item) {
            if (item.qty_shipping <= item.inventoryInfo.current_inventory && item.qty_shipping >= item.inventoryInfo.outgoing_inventory) return 'status status-ok'
            if (item.inventoryInfo.current_inventory > 0) return 'status status-warning'
        },
        selectItem(e, item) {
            if (e.target.checked) {
                item.selected = true;
                this.selectedAddress = item.address_id;
            } else {
                item.selected = false;
                this.selectedAddress = null;
                for (var i = 0; i < this.items.length; i++) {
                    if (this.items[i].selected) {
                        this.selectedAddress = this.items[i].address_id;
                    }
                }
            }
        },
        createShipment() {
            let items = [];

            for (var i = 0; i < this.items.length; i++) {
                if (this.items[i].selected) {
                    items[items.length] = {
                        id: this.items[i].id,
                        qty: this.items[i].qty_shipping
                    }
                }
            }
            if (items.length > 0) {
                this.selectedAddress = null;
                this.form.items = JSON.stringify(items);

                if (this.selectedPackage) {
                    this.form.post(this.route('admin.orders.packageadditems', this.selectedPackage))
                } else {
                    this.form.post(this.route('admin.orders.createusership', this.user.id))
                }
            }
        },
        createDropship() {
            let items = [];

            for (var i = 0; i < this.items.length; i++) {
                if (this.items[i].selected) {
                    items[items.length] = {
                        id: this.items[i].id,
                        qty: this.items[i].qty_shipping
                    }
                }
            }
            if (items.length > 0) {
                this.form.items = JSON.stringify(items);

                this.form.post(this.route('orders.createshipmentdropship', this.user.id), {
                    preserveScroll: true
                })
            }
        },
        createBackorder() {
            let items = [];

            for (var i = 0; i < this.items.length; i++) {
                if (this.items[i].selected) {
                    items[items.length] = {
                        id: this.items[i].id,
                        qty: this.items[i].qty_shipping
                    }
                }
            }
            if (items.length > 0) {
                this.form.items = JSON.stringify(items);
                this.form.post(this.route('orders.createshipmentbackorder', this.user.id), {
                    preserveScroll: true
                })
            }
        },
        addNote() {
            this.formNote.post(this.route('admin.orders.addnote', this.order.id), {
                preserveScroll: true
            })
        },
        addBox(shipment) {
            if (this.formAddBox.processing) return;

            this.formAddBox.get(this.route('admin.orders.createpackage', shipment), {
                preserveScroll: true
            })
        },
        confirmChangePayment(shipment) {
            const msg = _.get(this.$page.props, 'flash.error')
            if (msg && msg.indexOf('Error closing shipment') >= 0) {
                this.$confirm.require({
                    message: `${msg} Do you want to change the order to net-30 billing?`,
                    header: 'Error',
                    icon: 'text-danger pi pi-exclamation-triangle',
                    accept: () => {
                        axios.get(this.route('admin.orders.closeshipment', shipment), {
                            params: {
                                change_payment_type_code: 'month',
                            }
                        })
                            .then(() => {
                                this.$page.props.flash.success = _.get(res, 'data.success')
                                this.$inertia.reload()
                            })
                            .catch((error) => {
                                this.$page.props.flash.error = _.get(error, 'response.data.error')
                                this.$inertia.reload()
                            })
                    },
                });
            } else {
                this.$inertia.reload()
            }
        },
        closeShipment(shipment) {
            if (this.formCloseShipment.processing) return;
            axios.get(this.route('admin.orders.closeshipment', shipment))
                .then((res) => {
                    this.$page.props.flash.success = _.get(res, 'data.success')
                    this.$inertia.reload()
                })
                .catch((error) => {
                    this.$page.props.flash.error = _.get(error, 'response.data.error')
                    this.confirmChangePayment(shipment)
                })
        },
        reopenShipment(shipment) {
            if (this.formReopenShipment.processing) return;

            this.formReopenShipment.get(this.route('admin.orders.reopenshipment', shipment))
        },
        prepareShipment(shipment) {
            if (this.form.processing) return;

            this.form.post(this.route('admin.orders.prepareshipment', shipment));
        },
        cancelShipment(shipment) {
            if (this.formCancelShipment.processing) return;

            this.formCancelShipment.delete(this.route('admin.orders.cancelshipment', shipment.id), {
                preserveScroll: true
            });
        },
        setBox(box) {
            this.form.box_size = box.type;
            this.form.post(this.route('admin.orders.packageupdate', box), {
                preserveScroll: true
            })
        },
        getShipmentPackageOptions(shipment, box) {
            return shipment.packages.map((p, i) => ({
                label: `Package #${i + 1}`,
                value: p.id,
                id: p.id,
            })).filter(p => p.id != box.id)
        },
        moveToPackage(shipment, box, item) {
            if (item.moveToPackage) {
                this.formMovePackage.transform(() => ({
                    qty: item.qty,
                    old_package_id: box.id,
                    new_package_id: item.moveToPackage,
                    product_id: item.product_id,
                })).post(this.route('admin.orders.packagemoveitems', [shipment.id]), {
                    preserveScroll: true
                })
            }
        },
        setWeight(box) {
            this.form.box_size = box.type;
            this.form.weight = box.weight;
            this.form.post(this.route('admin.orders.packageupdate', box), {
                preserveScroll: true
            })
        },
        updateItem(item) {
            this.form.qty = item.qty;
            this.form.post(this.route('admin.orders.packageupdateitem', item), {
                preserveScroll: true
            })
        },
        deleteItem(item) {
            this.form.qty = 0;
            this.form.post(this.route('admin.orders.packageupdateitem', item), {
                preserveScroll: true
            })
        },
        moveItem(shipment, item, index) {
            if (shipment.packages[index]) {
                this.form.qty = 1;
                this.form.post(this.route('admin.orders.packagemoveitem', { package: shipment.packages[index].id, product: item.product_id }), {
                    preserveScroll: true
                })
            }
        },
        getRates(shipment) {
            this.inGetRate = true
            this.requestingRate = true;
            shipment.ratesFedex = [];
            shipment.ratesUps = [];
            axios.get(this.route('admin.orders.getrates', shipment)).then((response) => {
                shipment.ratesFedex = response.data.ratesFedex;
                shipment.ratesUps = response.data.ratesUps;
            })
                .finally(() => {
                    this.inGetRate = false
                    this.requestingRate = false;
                })
        },
        mtdSetShipApproved() {
            this.$inertia.form({
                _method: 'put',
                mtd_ship_approved: 1,
            }).post(route('admin.mtd.updateMtdFeatures', this.mtd.id), {
                preserveScroll: true
            });
        }
    },
}
</script>

<style>.gallery-thumbnail img {
    width: 120px;
}

.shipment {
    border: #000 1px solid;
}

.shipment-active .alert {
    position: fixed;
    bottom: 200px;
    right: 200px;
    z-index: 5000;
}

.packages-move {
    height: calc(1.6em + 0.75rem + 2px);
}

.packages-move button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.packages-move .p-inputtext {
    padding-top: 0.295rem !important;
    padding-bottom: 0.295rem !important;
}</style>
